import { useState } from 'react';
import type { HeroBannerItem } from '@/lib/heroBanners';

type Device = 'desktop' | 'tablet' | 'mobile';

const DEVICE_LIST: { key: Device; label: string; icon: string; maxWidth: string; height: string }[] = [
  { key: 'desktop', label: 'Ordinateur', icon: 'ri-computer-line', maxWidth: 'w-full', height: 'h-[220px]' },
  { key: 'tablet', label: 'Tablette', icon: 'ri-tablet-line', maxWidth: 'w-full max-w-[560px]', height: 'h-[220px]' },
  { key: 'mobile', label: 'Mobile', icon: 'ri-smartphone-line', maxWidth: 'w-full max-w-[300px]', height: 'h-[260px]' },
];

/**
 * Aperçu d'une bannière (image + textes + bouton) à différentes largeurs
 * d'écran, pour visualiser le rendu dans l'éditeur.
 */
export default function BannerEditorPreview({ item }: { item: HeroBannerItem }) {
  const [device, setDevice] = useState<Device>('desktop');
  const conf = DEVICE_LIST.find((d) => d.key === device) || DEVICE_LIST[0];

  const isEmpty = !item.image && !item.title && !item.subtitle && !item.description && !item.ctaLabel;
  const align = item.textAlign || 'center';
  const hasText = Boolean(item.title || item.subtitle || item.description || item.ctaLabel);
  const showText = item.showText !== false && hasText;
  const alignClass = align === 'left'
    ? 'items-start text-left mr-auto pl-5 md:pl-8'
    : align === 'right'
      ? 'items-end text-right ml-auto pr-5 md:pr-8'
      : 'items-center text-center mx-auto px-4';

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <i className="ri-eye-line text-accent-500"></i>
          <h4 className="text-sm font-semibold text-foreground-700">Aperçu du rendu</h4>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {DEVICE_LIST.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDevice(d.key)}
              title={d.label}
              className={`px-2.5 py-1.5 rounded-full flex items-center gap-1.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                device === d.key
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={d.icon}></i>
              <span className="hidden sm:inline">{d.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-3 flex justify-center">
        <div className={`${conf.maxWidth} rounded-md overflow-hidden border border-background-200/70`}>
          <div className={`relative ${conf.height} bg-foreground-900`}>
            {isEmpty ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <i className="ri-image-line text-2xl text-background-50/40"></i>
                <p className="text-xs text-background-50/60 mt-2">
                  Remplis l&apos;image ou le titre pour voir l&apos;aperçu.
                </p>
              </div>
            ) : (
              <>
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title || 'Bannière'}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-foreground-800 to-foreground-950"></div>
                )}
                {showText && (
                  <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/25 to-black/45"></div>
                )}
                {showText && (
                <div className={`relative h-full w-full flex flex-col justify-center max-w-xl mx-auto ${alignClass}`}>
                  {item.title && (
                    <h1 className="text-lg md:text-2xl font-bold font-heading text-white leading-tight drop-shadow">
                      {item.title}
                    </h1>
                  )}
                  {item.subtitle && (
                    <p className="mt-2 text-xs md:text-sm text-white/90 max-w-md leading-relaxed">
                      {item.subtitle}
                    </p>
                  )}
                  {item.description && (
                    <p className="mt-1.5 text-[11px] text-white/70 max-w-sm leading-relaxed hidden sm:block">
                      {item.description}
                    </p>
                  )}
                  {item.ctaLabel && (
                    <span className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap">
                      {item.ctaLabel}
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  )}
                </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}